/**
 * work-item-query-service.ts
 *
 * Query service providing specialized, bounded projections for Scrum, CRM,
 * and Calendar consumers from the authoritative WorkItem repository.
 */

import type { WorkItem, WorkItemType } from './work-item-contracts.js';
import { WorkItemRepository, globalWorkItemRepository } from './work-item-repository.js';

export interface DateRange {
  from: string;
  to: string;
}

export class WorkItemQueryService {
  constructor(private readonly repo: WorkItemRepository = globalWorkItemRepository) {}

  findById(id: string): WorkItem | undefined {
    const item = this.repo.findById(id);
    if (!item || item.archivedAt) return undefined;
    return item;
  }

  forWorkspace(workspaceId: string, statusId?: string): WorkItem[] {
    return this.repo.findMany((item) => {
      if (item.workspaceId !== workspaceId) return false;
      if (item.archivedAt) return false;
      if (statusId && item.statusId !== statusId) return false;
      return true;
    });
  }

  forScrum(workspaceId: string, sprintId?: string): WorkItem[] {
    const allowedTypes: WorkItemType[] = ['task', 'issue', 'milestone'];
    return this.repo.findMany((item) => {
      if (item.workspaceId !== workspaceId) return false;
      if (item.archivedAt) return false;
      if (!allowedTypes.includes(item.type)) return false;
      if (sprintId && item.source?.type === 'scrum' && item.source.id && item.source.id !== sprintId) {
        return false;
      }
      return true;
    });
  }

  forCrm(workspaceId: string, dealId?: string): WorkItem[] {
    return this.repo.findMany((item) => {
      if (item.workspaceId !== workspaceId) return false;
      if (item.archivedAt) return false;
      const isCrmType = item.type === 'deal-follow-up' || item.type === 'customer-action' || item.source?.type === 'crm';
      if (!isCrmType) return false;
      if (dealId && item.source?.id && item.source.id !== dealId) {
        return false;
      }
      return true;
    });
  }

  forCalendar(workspaceId: string, range?: DateRange): WorkItem[] {
    const fromMs = range ? new Date(range.from).getTime() : 0;
    const toMs = range ? new Date(range.to).getTime() : Infinity;

    return this.repo.findMany((item) => {
      if (item.workspaceId !== workspaceId) return false;
      if (item.archivedAt) return false;
      const dateStr = item.scheduledAt || item.dueAt;
      if (!dateStr) return false;
      const itemMs = new Date(dateStr).getTime();
      if (isNaN(itemMs)) return false;
      return itemMs >= fromMs && itemMs <= toMs;
    });
  }
}

export const globalWorkItemQueryService = new WorkItemQueryService();
